import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { wrestlerAvatar, playerAvatar } from "../lib/api";
import AvatarLayered from "../components/AvatarLayered";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Swords, Trophy, X, Zap, Users2, Star, Briefcase, Minimize2, Maximize2, Cog, Check, ArrowUp, ArrowDown, Lock } from "lucide-react";
import ChatBox from "../components/ChatBox";
import { PromotionWarsCard, PromotionWarsModal } from "../components/PromotionWars";
import { Panel, PageSparkles, RankOneBadge, SelectedTag } from "../components/pvp/PvpUI";
import { SectionHeading } from "../components/wrestler/WrestlerUI";
import { ClassIcon } from "../components/ClassIcon";
import { Flame, Users, Timer, Skull, Landmark, BicepsFlexed } from "lucide-react";
import { visibleInterval } from "../lib/visibleInterval";

const SHOW_META = {
  proving_ground:   { color: "#00F0FF", label: "PROVING GROUND",   img: "/pvp/show_proving_ground.webp",   regular: true },
  free_4_all:       { color: "#FF007F", label: "FREE 4 ALL",       img: "/pvp/show_free_4_all.webp",       regular: true },
  tag_team_turmoil: { color: "#39FF14", label: "TAG TEAM TURMOIL", img: "/pvp/show_tag_team_turmoil.webp", regular: true },
  rumble_royale:    { color: "#FFE600", label: "RUMBLE ROYALE",    img: "/pvp/show_rumble_royale.webp",    regular: false },
  titleshot_bank:   { color: "#9D00FF", label: "TITLESHOT IN THE BANK", img: "/pvp/show_titleshot_bank.webp", regular: false },
  promotion_wars:   { color: "#FF9500", label: "PROMOTION WARS",   img: "/pvp/show_promotion_wars.webp",   regular: false },
};
const TITLE_IMG = {
  heavyweight:   "/pvp/title_heavyweight.webp",
  middleweight:  "/pvp/title_middleweight.webp",
  cruiserweight: "/pvp/title_cruiserweight.webp",
  tag_team:      "/pvp/title_tag_team.webp",
};

const DIV_LABEL = { heavyweight: "HEAVYWEIGHT", middleweight: "MIDDLEWEIGHT", cruiserweight: "CRUISERWEIGHT", tag_team: "TAG TEAM" };
const DIV_COLOR = { heavyweight: "#FF007F", middleweight: "#00F0FF", cruiserweight: "#FFD700", tag_team: "#39FF14" };

/**
 * StatDelta — wraps a single "W 12", "L 3", "HOF 5"-style stat label and
 * pops a tiny colored arrow (green up / red down / purple up) upward whenever
 * the underlying value increases. The value itself keeps updating normally.
 */
function StatDelta({ value, tone = "up-green", children }) {
  const prevRef = useRef(value);
  const [floats, setFloats] = useState([]);
  useEffect(() => {
    if (typeof value === "number" && typeof prevRef.current === "number" && value > prevRef.current) {
      const id = Date.now() + Math.random();
      setFloats(f => [...f, { id, delta: value - prevRef.current }]);
      setTimeout(() => setFloats(f => f.filter(x => x.id !== id)), 1000);
    }
    prevRef.current = value;
  }, [value]);
  const color = tone === "down-red" ? "#FF007F" : tone === "up-purple" ? "#9D00FF" : "#39FF14";
  const Arrow = tone === "down-red" ? ArrowDown : ArrowUp;
  return (
    <span className="relative inline-flex items-center">
      {children}
      {floats.map(f => (
        <span
          key={f.id}
          className="float-arrow absolute left-1/2 -top-2 pointer-events-none z-10 font-heading text-[13px] flex items-center gap-0.5 whitespace-nowrap"
          style={{ color, textShadow: `0 0 10px ${color}, 0 0 4px ${color}` }}
        >
          <Arrow size={14} strokeWidth={3.5} />
          <span>+{f.delta}</span>
        </span>
      ))}
    </span>
  );
}

function Countdown({ iso, status, countdownEnds, live }) {
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 500); return () => clearInterval(id); }, []);
  if (live || status === "live") {
    // Show is currently running/simulating — green pulsing dot + LIVE text
    return (
      <span className="inline-flex items-center gap-1 font-heading" data-testid="show-live-indicator">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ background: "#39FF14", boxShadow: "0 0 6px #39FF14" }}
        />
        <span style={{ color: "#39FF14", textShadow: "0 0 6px #39FF14" }}>LIVE</span>
      </span>
    );
  }
  if (status === "countdown") {
    const diff = Math.max(0, new Date(countdownEnds).getTime() - Date.now());
    const s = Math.floor(diff / 1000);
    return <span className="font-mono text-sm neon-yellow">CD {Math.floor(s/60)}:{String(s%60).padStart(2,"0")}</span>;
  }
  const diff = Math.max(0, new Date(iso).getTime() - Date.now());
  const s = Math.floor(diff / 1000);
  return <span className="font-mono text-xs neon-cyan">{Math.floor(s/60)}:{String(s%60).padStart(2,"0")}</span>;
}

function StatIcon({ icon: Icon, color, value, title, big, testId }) {
  return (
    <span className="inline-flex items-center gap-0.5" title={title} data-testid={testId}>
      <Icon size={big ? 14 : 11} strokeWidth={2.6} style={{ color, filter: `drop-shadow(0 0 3px ${color})` }} />
      <span className={`font-num leading-none ${big ? "text-sm" : "text-xs"}`} style={{ color }}>{Number(value).toLocaleString()}</span>
    </span>
  );
}

function ShowName({ name, color }) {
  const m = /^(.*?)\s*(#\d+)$/.exec(name || "");
  const base = m ? m[1] : name;
  return (
    <div className="min-w-0 flex items-baseline gap-1 font-graffiti text-sm leading-tight" style={{ color, textShadow: `0 0 8px ${color}88` }} title={name}>
      <span className="truncate">{base}</span>
      {m && <span className="shrink-0 font-num text-sm">{m[2]}</span>}
    </div>
  );
}

function ChampionsBar({ champions, onOpenHolder }) {
  return (
    <div className="grid grid-cols-4 gap-2 mb-2" data-testid="champions-bar">
      {["heavyweight", "middleweight", "cruiserweight", "tag_team"].map((div) => {
        const c = champions[div];
        const color = DIV_COLOR[div];
        const isTag = div === "tag_team";
        const teamMembers = c?.holder_team_members || [];
        const holderWid = c?.holder_wrestler_id;
        const av = c?.holder_avatar_updated_at;
        const avUrl = holderWid ? `${api.defaults.baseURL}/wrestlers/${holderWid}/avatar?v=${new Date(av || 0).getTime()}` : null;
        return (
          <button
            key={div}
            data-testid={`champ-${div}`}
            onClick={() => onOpenHolder(c)}
            className="wr-rise rounded-xl p-1.5 border-2 flex items-center gap-1.5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 text-left overflow-hidden"
            style={{ borderColor: color + "99", background: `linear-gradient(135deg, ${color}22, #0A051AEE 60%)`, boxShadow: `0 0 14px ${color}33` }}
          >
            <img src={TITLE_IMG[div]} alt={div} className="w-9 h-9 rounded-sm object-cover flex-shrink-0" style={{ boxShadow: `0 0 10px ${color}88` }} />
            {isTag && teamMembers.length > 0 ? (
              <div className="flex items-center -space-x-2 flex-shrink-0" data-testid={`champ-team-avatars-${div}`}>
                {teamMembers.map((m, i) => (
                  <AvatarLayered showCrown
                    key={m.wrestler_id || i}
                    fgSrc={`${api.defaults.baseURL}/wrestlers/${m.wrestler_id}/avatar?v=${new Date(m.avatar_updated_at || 0).getTime()}`}
                    promo={m.promotion}
                    className="w-9 h-9 rounded-md border-2"
                    style={{ borderColor: color, boxShadow: `0 0 6px ${color}66`, zIndex: teamMembers.length - i }}
                    testId={`champ-team-avatar-${div}-${i}`}
                  />
                ))}
              </div>
            ) : avUrl && (
              <AvatarLayered showCrown
                fgSrc={avUrl}
                promo={c?.holder_promotion}
                className="w-9 h-9 rounded-md border flex-shrink-0"
                style={{ borderColor: color, boxShadow: `0 0 6px ${color}66` }}
                testId={`champ-avatar-${div}`}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color }}>{DIV_LABEL[div]} CHAMP</div>
              <div className="font-graffiti text-sm leading-tight truncate" style={{ textShadow: `0 0 8px ${color}66` }}>
                {isTag
                  ? (c?.holder_team_name || (teamMembers.length ? teamMembers.map(m => m.wrestler_name).join(" & ") : "VACANT"))
                  : (c?.holder_wrestler_name || "VACANT")}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ShowCard({ show, canRegister, onRegister, onOpenRoster, flash }) {
  const meta = SHOW_META[show.show_type] || {};
  const expired = show.status === "open" && !!show.ends_at && new Date(show.ends_at).getTime() <= Date.now();
  const disabled = show.is_registered || show.status !== "open" || expired || !canRegister;
  // Detect a fresh registration transition (false → true) to fire the check pulse once.
  const [justRegistered, setJustRegistered] = useState(false);
  const prevRegisteredRef = useRef(show.is_registered);
  useEffect(() => {
    if (!prevRegisteredRef.current && show.is_registered) {
      setJustRegistered(true);
      const t = setTimeout(() => setJustRegistered(false), 650);
      return () => clearTimeout(t);
    }
    prevRegisteredRef.current = show.is_registered;
  }, [show.is_registered]);

  const registeredView = show.is_registered;
  let label = "REGISTER";
  if (registeredView) label = null;                    // rendered as ✓ REGISTERED below
  else if (show.status === "countdown") label = "STARTING";
  else if (show.status === "live" || expired) label = "SIMULATING";
  else if (show.filled >= show.slots) label = "FULL";
  else if (!canRegister) label = "INELIGIBLE";
  return (
    <div
      data-testid={`show-card-${show.show_type}`}
      className="pvp-show group relative rounded-xl p-[2px] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5"
      style={{ background: `linear-gradient(150deg, ${meta.color}, #5B3BFF55, ${meta.color}88)`, boxShadow: flash ? `0 0 24px ${meta.color}` : `0 0 12px ${meta.color}33` }}
    >
     <div className="rounded-[10px] bg-[#110726]/95 p-2 h-full">
      <div className="pvp-show-img rounded-lg mb-1.5 flex items-center justify-center relative overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${meta.color}22, #0A051A)`, border: `1px solid ${meta.color}66` }}>
        {meta.img
          ? <img src={meta.img} alt={meta.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          : <div className="text-4xl">🏟️</div>}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(10,5,26,0.75))" }} />
        <button
          data-testid={`show-slots-${show.show_type}`}
          onClick={() => onOpenRoster(show)}
          className="absolute bottom-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-[#00F0FF]/60 bg-black/70 backdrop-blur font-num text-xs text-[#00F0FF] hover:bg-[#00F0FF]/25 transition-colors"
          style={{ boxShadow: "0 0 8px #00F0FF55" }}
        >
          <Users size={10} /> {show.filled}/{show.slots}
        </button>
        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur border border-white/10 flex items-center gap-1">
          <Timer size={10} className="text-[#00F0FF]" />
          <Countdown
            iso={show.ends_at}
            countdownEnds={show.countdown_ends_at}
            status={show.status}
            live={show.status === "live" || show.filled >= show.slots || (show.ends_at && new Date(show.ends_at).getTime() <= Date.now())}
          />
        </div>
      </div>
      <div className="flex items-center gap-1 mb-1.5">
        <ShowName name={show.name} color={meta.color} />
      </div>
      {registeredView ? (
        <div
          data-testid={`show-registered-badge-${show.show_type}`}
          className={`w-full text-center rounded-md py-1.5 font-heading text-[10px] uppercase tracking-widest border-2 bg-[#0A051A] ${justRegistered ? "reg-check-pulse" : ""}`}
          style={{ color: "#39FF14", borderColor: "#39FF14", boxShadow: "0 0 10px rgba(57,255,20,0.35)" }}
        >
          <span className="inline-flex items-center gap-1">
            <Check size={13} strokeWidth={3} /> REGISTERED
          </span>
        </div>
      ) : (
        <button
          data-testid={`show-register-${show.show_type}`}
          disabled={disabled}
          onClick={() => onRegister(show)}
          className={`btn-neon w-full text-[10px] py-1.5 ${flash ? "register-ready-glow" : ""}`}
        >
          {label}
        </button>
      )}
     </div>
    </div>
  );
}

function MatchCard({ a, b, winner, kind }) {
  const winA = winner && (winner === "a" || winner === a?.user_id || winner === a?.wrestler_id || winner === a?.team_id);
  const winB = winner && !winA;
  const nav = useNavigate();
  const side = (side, r, isWin) => {
    const glow = isWin ? "#39FF14" : "#39194D";
    const label = isWin ? "WINNER" : (winner ? "LOSS" : "");
    const members = r?.members || [];
    return (
      <div
        onClick={() => r?.account_name && nav(`/profile/${encodeURIComponent(r.account_name)}`)}
        className={`flex-1 min-w-0 flex flex-col items-center gap-1 p-2 rounded-lg border ${r?.account_name ? "cursor-pointer hover:brightness-110" : ""}`}
        style={{
          borderColor: isWin ? glow : "#39194D",
          background: isWin ? "#39FF1418" : "#0A051A",
          boxShadow: isWin ? `0 0 12px ${glow}55` : undefined,
        }}
        data-testid={`match-side-${side}`}
      >
        {kind === "2v2" && members.length ? (
          <div className="flex gap-1">
            {members.map((m, i) => (
              <AvatarLayered showCrown key={i} fgSrc={`${api.defaults.baseURL}/wrestlers/${m.wrestler_id}/avatar?v=${new Date(m.avatar_updated_at || 0).getTime()}`} promo={m.promotion} className="w-12 h-12 rounded-md border border-[#39194D]" />
            ))}
          </div>
        ) : r?.wrestler_id ? (
          <AvatarLayered showCrown fgSrc={`${api.defaults.baseURL}/wrestlers/${r.wrestler_id}/avatar?v=${new Date(r.avatar_updated_at || 0).getTime()}`} promo={r.promotion} className="w-16 h-16 rounded-md border border-[#39194D]" />
        ) : (
          <div className="w-16 h-16 rounded-md bg-[#12082B] flex items-center justify-center font-heading text-2xl text-[#3D2A5A]">?</div>
        )}
        <div className="font-heading text-[11px] text-center truncate w-full">{r?.team_name || r?.wrestler_name || "empty slot"}</div>
        {kind === "2v2" && members.length ? (
          <div className="text-[8px] font-mono text-[#A89BCC] truncate w-full text-center">{members.map(m => m.wrestler_name).join(" & ")}</div>
        ) : (
          <div className="text-[9px] font-mono text-[#A89BCC] truncate w-full text-center">{r?.account_name || "—"}</div>
        )}
        {label && (
          <div className="text-[9px] font-heading uppercase tracking-widest" style={{ color: isWin ? "#39FF14" : "#FF007F" }}>{label}</div>
        )}
      </div>
    );
  };
  return (
    <div className="flex items-center gap-2">
      {side("a", a, winA)}
      <div className="font-heading text-lg neon-pink px-1" style={{ textShadow: "0 0 8px #FF007F" }}>VS</div>
      {side("b", b, winB)}
    </div>
  );
}

function BattleGrid({ participants, winner, slots }) {
  const total = slots || participants?.length || 0;
  const arr = [...(participants || [])];
  while (arr.length < total) arr.push(null);
  const isWinner = (r) => r && winner && (r.wrestler_id === winner || r.user_id === winner);
  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="battle-grid">
      {arr.map((r, i) => {
        const w = isWinner(r);
        return (
          <div
            key={i}
            data-testid={`battle-slot-${i}`}
            className="flex flex-col items-center gap-1 p-1.5 rounded-md border"
            style={{
              borderColor: w ? "#39FF14" : (r ? "#39194D" : "#3D2A5A55"),
              background: w ? "#39FF1418" : (r ? "#0A051A" : "#0A051A88"),
              boxShadow: w ? "0 0 12px #39FF1466" : undefined,
            }}
          >
            {r?.wrestler_id ? (
              <AvatarLayered showCrown fgSrc={`${api.defaults.baseURL}/wrestlers/${r.wrestler_id}/avatar?v=${new Date(r.avatar_updated_at || 0).getTime()}`} promo={r.promotion} className="w-12 h-12 rounded-md border border-[#39194D]" />
            ) : (
              <div className="w-12 h-12 rounded-md bg-[#12082B] flex items-center justify-center font-heading text-lg text-[#3D2A5A]">?</div>
            )}
            <div className="font-heading text-[9px] truncate w-full text-center">{r?.wrestler_name || "empty"}</div>
            <div className="text-[8px] font-mono text-[#A89BCC] truncate w-full text-center">{r?.account_name || "—"}</div>
            {w && <div className="text-[8px] font-heading uppercase tracking-widest text-[#39FF14]" style={{ textShadow: "0 0 6px #39FF14" }}>WINNER</div>}
          </div>
        );
      })}
    </div>
  );
}

function RosterModal({ show, onClose }) {
  const [regs, setRegs] = useState(null);
  const [completed, setCompleted] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!show) return;
    if (show.matches) {
      // Completed results already provided
      setCompleted(show);
      setRegs(null);
      return;
    }
    api.get(`/shows/${show.show_id}/roster`).then((r) => setRegs(r.data)).catch(() => setRegs({ slots: 0, registered: [] }));
    setCompleted(null);
  }, [show?.show_id, show?.matches]);
  if (!show) return null;
  if (show.show_type === "promotion_wars") return <PromotionWarsModal show={show} onClose={onClose} />;
  const isBattleRoyal = (show.match_type || "").toLowerCase() === "battle_royal" || (show.match_type || "").toLowerCase() === "ladder";

  // Build pairings for open shows so we still show a match-card style preview
  const buildPairs = () => {
    const filled = regs?.registered || [];
    const total = show.slots;
    const pairs = [];
    for (let i = 0; i < total; i += 2) {
      pairs.push([filled[i] || null, filled[i + 1] || null]);
    }
    return pairs;
  };
  const matches = completed?.matches || (regs ? buildPairs().map(([a, b]) => ({ a, b, winner: null, kind: show.match_type || "1v1" })) : []);
  const matchKind = show.match_type || matches[0]?.kind || "1v1";

  // Battle-royal / ladder path: show a wide grid instead of VS pairings
  if (isBattleRoyal) {
    // Rumble Royale / Titleshot Bank completed results carry the winner + full participant list under show.results
    const winnerObj = completed?.results?.winner || show.results?.winner;
    const participants = (completed?.results?.participants && completed.results.participants.length > 0)
      ? completed.results.participants
      : (show.results?.participants || completed?.matches?.[0]?.a?.members || regs?.registered || []);
    const winnerId = winnerObj?.wrestler_id || winnerObj?.user_id;
    const losers = winnerId ? participants.filter(p => (p?.wrestler_id || p?.user_id) !== winnerId) : participants;
    const showType = show.show_type;
    const isCompleted = !!winnerObj;
    return (
      <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="show-roster-modal" onClick={onClose}>
        <div className="card-surface p-4 w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base neon-yellow uppercase tracking-widest">{show.name} · {show.match_type === "battle_royal" ? "BATTLE ROYAL" : "LADDER MATCH"}</h3>
            <button data-testid="roster-modal-close" onClick={onClose} className="p-1 rounded-lg border border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10"><X size={16} /></button>
          </div>

          {isCompleted && winnerObj ? (
            <>
              {/* Hero winner */}
              <div
                className={`flex flex-col items-center mb-5 mt-2 ${winnerObj?.account_name ? "cursor-pointer group" : ""}`}
                data-testid="br-winner-hero"
                onClick={() => { if (winnerObj?.account_name) { onClose(); nav(`/profile/${encodeURIComponent(winnerObj.account_name)}`); } }}
                title={winnerObj?.account_name ? `View ${winnerObj.account_name}` : ""}
              >
                <div className="text-[10px] font-heading uppercase tracking-[0.3em] text-[#39FF14] mb-2" style={{ textShadow: "0 0 8px #39FF14" }}>
                  {showType === "titleshot_bank" ? "🎉 BRIEFCASE WINNER" : "🏆 RUMBLE CHAMPION"}
                </div>
                <div
                  className="relative rounded-2xl p-1 transition-transform group-hover:scale-105"
                  style={{
                    background: "radial-gradient(circle, #39FF1466 0%, transparent 70%)",
                    boxShadow: "0 0 40px #39FF1499, 0 0 80px #39FF1444",
                    animation: "pulse 2s ease-in-out infinite",
                  }}
                >
                  <AvatarLayered showCrown
                    fgSrc={`${api.defaults.baseURL}/wrestlers/${winnerObj.wrestler_id}/avatar?v=${new Date(winnerObj.avatar_updated_at || 0).getTime()}`}
                    promo={winnerObj.promotion}
                    alt={winnerObj.wrestler_name}
                    className="w-36 h-36 rounded-2xl border-4"
                    style={{ borderColor: "#39FF14", boxShadow: "0 0 24px #39FF14" }}
                  />
                </div>
                <div className="mt-3 font-heading text-2xl uppercase tracking-widest group-hover:brightness-125" style={{ color: "#39FF14", textShadow: "0 0 10px #39FF14" }}>{winnerObj.wrestler_name}</div>
                <div className="text-[11px] font-mono text-[#A89BCC] mt-0.5 group-hover:text-[#00F0FF]">{winnerObj.account_name || ""}</div>
              </div>

              {/* Losers grid */}
              {losers.length > 0 && (
                <>
                  <div className="text-[10px] font-heading uppercase tracking-[0.25em] text-[#FF007F] mb-2 border-b border-[#FF007F]/25 pb-1">
                    ELIMINATED · {losers.length}
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 opacity-80" data-testid="br-losers-grid">
                    {losers.map((r, i) => (
                      <div
                        key={r?.wrestler_id || i}
                        data-testid={`br-loser-${i}`}
                        onClick={() => { if (r?.account_name) { onClose(); nav(`/profile/${encodeURIComponent(r.account_name)}`); } }}
                        className={`flex flex-col items-center gap-1 p-1.5 rounded-md border transition ${r?.account_name ? "cursor-pointer hover:opacity-100 hover:brightness-125 hover:border-[#00F0FF]" : ""}`}
                        style={{ borderColor: "#3D2A5A", background: "#0A051A" }}
                        title={r?.account_name ? `View ${r.account_name}` : ""}
                      >
                        {r?.wrestler_id ? (
                          <AvatarLayered showCrown fgSrc={`${api.defaults.baseURL}/wrestlers/${r.wrestler_id}/avatar?v=${new Date(r.avatar_updated_at || 0).getTime()}`} promo={r.promotion} className="w-11 h-11 rounded-md border border-[#39194D]" fgClassName="grayscale" />
                        ) : (
                          <div className="w-11 h-11 rounded-md bg-[#12082B]" />
                        )}
                        <div className="font-heading text-[9px] truncate w-full text-center text-[#A89BCC]">{r?.wrestler_name || "empty"}</div>
                        <div className="text-[8px] font-mono text-[#6B5A8F] truncate w-full text-center">{r?.account_name || "—"}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <BattleGrid participants={participants} winner={winnerObj?.wrestler_id || winnerObj} slots={show.slots} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="show-roster-modal" onClick={onClose}>
      <div className="card-surface p-4 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3 sticky top-0 z-10 bg-[#12082B] py-1">
          <h3 className="font-heading text-base neon-cyan uppercase tracking-widest">{show.name}</h3>
          <button data-testid="roster-modal-close" onClick={onClose} className="p-1 rounded-lg border border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10"><X size={16} /></button>
        </div>
        <div className="flex flex-col gap-3">
          {matches.length === 0 && (
            <div className="text-center text-[#A89BCC] text-sm font-mono py-4">Loading…</div>
          )}
          {matches.map((m, i) => {
            const kind = m.kind || matchKind;
            let winner = null;
            if (m.winner_user_id) winner = m.winner_user_id;
            if (m.winner_team_id) winner = m.winner_team_id;
            return (
              <div key={i} className="p-2 rounded-md bg-[#0A051A] border border-[#39194D]" data-testid={`roster-match-${i}`}>
                <MatchCard a={m.a} b={m.b} winner={winner} kind={kind} />
                {m.result_tag && (
                  <div className="text-[9px] font-heading uppercase tracking-widest text-center mt-1" style={{ color: m.result_tag === "UPSET" ? "#FFE600" : m.result_tag === "DECISIVE" ? "#39FF14" : "#00F0FF" }}>
                    {m.result_tag === "UPSET" ? "UPSET WIN" : m.result_tag === "DECISIVE" ? "DECISIVE VICTORY" : "EVENLY MATCHED"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WrestlerSelectCard({ w, active, onSelect, team, partner, onOpenTagTeam }) {
  const isWildcard = w.class_id === "wildcard";
  const isTagTeam = w.class_id === "tag_team" || w.class_id === "tag_team_1";
  return (
    <div
      data-testid={`pvp-wrestler-${w.wrestler_id}`}
      onClick={() => onSelect(w)}
      className={`relative p-2 rounded-xl border-2 flex items-center gap-2 cursor-pointer overflow-hidden transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 ${active ? "pl-7 border-[#FFE600] shadow-[0_0_16px_#FFE60077]" : "border-[#2D1C52] hover:border-[#00F0FF]/70"}`}
      style={active
        ? { backgroundImage: "linear-gradient(90deg, rgba(10,5,26,0.35), rgba(10,5,26,0.8)), url('/backgroundgraphics/Wrestlerpage_Background.webp')", backgroundSize: "cover", backgroundPosition: "center" }
        : { background: "#0A051A" }}
      aria-selected={active}
    >
      {active && <SelectedTag />}
      <div className="rounded-lg p-[2px] shrink-0" style={{ background: active ? "linear-gradient(140deg, #FFE600, #FF007F)" : "linear-gradient(140deg, #5B3BFF, #00F0FF)" }}>
        <AvatarLayered showCrown fgSrc={wrestlerAvatar(w)} promo={w.owner_promotion} className="w-[64px] h-[64px] rounded-md" />
      </div>
      {isTagTeam && (
        <div className="flex flex-col items-center -ml-1" data-testid={`pvp-tt-partner-slot-${w.wrestler_id}`}>
          {partner?.wrestler ? (
            <AvatarLayered showCrown
              fgSrc={wrestlerAvatar(partner.wrestler)}
              promo={partner.wrestler.owner_promotion}
              alt={partner.wrestler.name}
              className="w-[73px] h-[73px] rounded-md border border-[#39FF14]"
              style={{ boxShadow: "0 0 8px #39FF1466" }}
              title={`Partner: ${partner.wrestler.name}`}
            />
          ) : (
            <div className="w-[73px] h-[73px] rounded-md bg-[#12082B] border-2 border-dashed border-[#39FF14] flex flex-col items-center justify-center text-[#39FF14]" title="No tag team partner">
              <div className="text-3xl leading-none">?</div>
              <div className="text-[8px] font-mono uppercase mt-0.5">Empty</div>
            </div>
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-graffiti text-base leading-tight truncate" style={{ textShadow: "1px 1px 0 #000" }}>{w.name}</div>
        <div className="text-[10px] font-mono flex items-center gap-1" style={{ color: w.class_info?.color || "#A89BCC" }} data-testid={`pvp-wrestler-class-${w.wrestler_id}`}>
          <span className="font-num text-xs">LV {w.level}</span> ·
          <ClassIcon classId={w.class_info?.id || w.class_id} color={w.class_info?.color || "#A89BCC"} size={11} />
          <span>{w.class_info?.name}</span>
        </div>
        <div className="text-[10px] font-mono flex items-center gap-1.5">
          <StatDelta value={w.pvp_wins} tone="up-green">
            <StatIcon icon={Swords} color="#39FF14" value={w.pvp_wins} title="PvP Wins" />
          </StatDelta>
          <StatDelta value={w.pvp_losses} tone="down-red">
            <StatIcon icon={Skull} color="#FF007F" value={w.pvp_losses} title="PvP Losses" />
          </StatDelta>
          {w.hof_points > 0 && (
            <StatDelta value={w.hof_points} tone="up-purple">
              <StatIcon icon={Landmark} color="#B44DFF" value={w.hof_points} title="HOF Points" />
            </StatDelta>
          )}
          <StatIcon icon={BicepsFlexed} color="#FF9500" value={w.stats?.power ?? 0} title="Power" testId={`pvp-wrestler-power-${w.wrestler_id}`} />
        </div>
        {isWildcard && (
          <div className="mt-1 flex gap-0.5" data-testid={`wc-div-${w.wrestler_id}`}>
            {["heavyweight", "middleweight", "cruiserweight"].map(d => (
              <button
                key={d}
                onClick={async (e) => { e.stopPropagation(); await api.post(`/pvp/wildcard-division/${w.wrestler_id}`, { division: d }); onSelect(w, d); }}
                className={`px-1 py-0.5 rounded text-[9px] font-heading uppercase ${w.wildcard_division === d ? "bg-[#9D00FF] text-black" : "bg-[#12082B] text-[#A89BCC]"}`}
                title={`Set division: ${d}`}
              >
                {({ heavyweight: "HW", middleweight: "MW", cruiserweight: "CW" })[d]}
              </button>
            ))}
          </div>
        )}
        {isTagTeam && team && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenTagTeam(team.team_id); }}
            className="mt-1 text-[9px] font-mono neon-green truncate hover:underline"
            title={`Tag Team: ${team.name}`}
            data-testid={`pvp-tt-name-${w.wrestler_id}`}
          >
            🤝 {team.name}
          </button>
        )}
      </div>
    </div>
  );
}

function ContendersBlock({ selected, myTeam, nextTitleShotAt }) {
  const [data, setData] = useState(null);
  const prevRanks = useRef({});
  const [arrows, setArrows] = useState({});       // persistent arrow direction per wrestler
  const [flashes, setFlashes] = useState({});     // transient move-up/down animation trigger
  const [now, setNow] = useState(Date.now());
  const nav = useNavigate();
  const openProfile = (name) => name && nav(`/profile/${encodeURIComponent(name)}`);

  // Determine which division to show for the selected wrestler
  const division = useMemo(() => {
    if (!selected) return null;
    if (selected.class_id === "wildcard") return selected.wildcard_division || "heavyweight";
    if (selected.class_id === "tag_team" || selected.class_id === "tag_team_1") return "tag_team";
    if (selected.class_id === "heavyweight" || selected.class_id === "middleweight" || selected.class_id === "cruiserweight") return selected.class_id;
    return null;
  }, [selected]);

  useEffect(() => {
    if (!division || !selected) { setData(null); return; }
    let cancel = false;
    const fetchList = async () => {
      try {
        const r = await api.get(`/pvp/contenders/${division}?wrestler_id=${selected.wrestler_id}`);
        if (cancel) return;
        // Diff ranks — flash for the animation, arrows are PERSISTENT until next change.
        // For tag team divisions the row key is team_id; for singles it's wrestler_id.
        const newFlashes = {};
        const rowKey = (row) => row.team_id || row.wrestler_id;
        const combined = [...(r.data.top || [])];
        if (r.data.self) combined.push(r.data.self);
        const visibleKeys = new Set(combined.map(rowKey));
        combined.forEach(row => {
          const key = rowKey(row);
          const prev = prevRanks.current[key];
          if (prev != null && prev !== row.rank) {
            const dir = prev > row.rank ? "up" : "down";
            newFlashes[key] = dir;
            setArrows(a => ({ ...a, [key]: dir }));
            setTimeout(() => setFlashes((a) => { const { [key]: _drop, ...rest } = a; return rest; }), 1600);
          }
          prevRanks.current[key] = row.rank;
        });
        if (Object.keys(newFlashes).length) setFlashes((a) => ({ ...a, ...newFlashes }));
        // Purge persistent arrows for rows no longer in the list
        setArrows(a => {
          const kept = {};
          Object.keys(a).forEach(k => { if (visibleKeys.has(k)) kept[k] = a[k]; });
          return kept;
        });
        setData(r.data);
      } catch (e) { /* silent */ }
    };
    fetchList();
    const stop = visibleInterval(fetchList, 8000);
    return () => { cancel = true; stop(); };
  }, [division, selected?.wrestler_id]);

  // Countdown ticker for next-title-shot
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!selected) return null;
  if (!division) {
    return (
      <div className="mt-3 card-surface p-3" data-testid="contenders-block">
        <h3 className="font-heading text-sm neon-cyan uppercase tracking-widest mb-1">Top Contenders</h3>
        <div className="text-[10px] font-mono text-[#A89BCC]">Select a wrestler to view contender rankings.</div>
      </div>
    );
  }
  if (!data) return null;
  const rows = [...(data.top || [])];
  if (data.self && !rows.some(r => r.wrestler_id === data.self.wrestler_id)) {
    rows.push({ ...data.self, __self: true });
  }
  const color = DIV_COLOR[division];
  const remaining = nextTitleShotAt ? Math.max(0, new Date(nextTitleShotAt).getTime() - now) : 0;
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return (
    <div className="mt-4" data-testid="contenders-block">
      <div className="flex items-center gap-2 mb-1">
        <SectionHeading icon={Trophy} color={color} small className="mb-0 flex-1">Top Contenders</SectionHeading>
        {nextTitleShotAt && (
          <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-md border" data-testid="titleshot-countdown"
                style={{ borderColor: "#FFE60088", color: "#FFE600", background: "#0A051A" }}
                title="Next title shot fires when this hits 0">
            ⏱ {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
          </span>
        )}
      </div>
      <div className="text-[9px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color }}>{DIV_LABEL[division]}</div>
      <div className="flex flex-col gap-1" data-testid="contenders-list">
        {rows.length === 0 && (
          <div className="text-[10px] font-mono text-[#A89BCC]">No contenders yet — earn points via PvP wins.</div>
        )}
        {rows.map((r) => {
          const isTeam = data.grouping === "team" || !!r.members;
          const rowKey = (r.team_id || r.wrestler_id) + "-" + r.rank;
          const arrowKey = r.team_id || r.wrestler_id;
          const isMe = r.__self || (data.self && (data.self.team_id ? data.self.team_id === r.team_id : data.self.wrestler_id === r.wrestler_id));
          const isTopSelf = data.self && (data.self.team_id ? data.self.team_id === r.team_id : data.self.wrestler_id === r.wrestler_id) && !r.__self;
          const arrow = arrows[arrowKey];
          const flash = flashes[arrowKey];
          const first = r.rank === 1;
          return (
            <div
              key={rowKey}
              data-testid={`contender-row-${r.rank}`}
              className={`relative flex items-center gap-2 rounded-xl border-2 transition-all overflow-hidden ${first ? "p-2 pvp-rank1-card" : "p-1.5"} ${isMe || isTopSelf ? "border-[#FFE600] bg-[#FFE600]/10" : first ? "border-[#FFD700]" : "border-[#2D1C52] bg-[#0A051A]"} ${flash === "up" ? "animate-move-up" : flash === "down" ? "animate-move-down" : ""}`}
              style={first && !(isMe || isTopSelf) ? { background: "linear-gradient(120deg, #FFD70033, #0A051A 55%, #FF007F22)" } : undefined}
            >
              {first ? <RankOneBadge testId="contender-rank1-badge" /> : (
                <div className="w-6 text-center font-num text-base" style={{ color: r.rank <= 3 ? color : "#A89BCC" }}>{r.rank}</div>
              )}
              {arrow && (
                <span
                  data-testid={`contender-arrow-${arrowKey}`}
                  className="text-xs font-bold"
                  style={{
                    color: arrow === "up" ? "#39FF14" : "#FF007F",
                    textShadow: `0 0 6px ${arrow === "up" ? "#39FF14" : "#FF007F"}`,
                  }}
                  aria-label={arrow === "up" ? "moved up" : "moved down"}
                >
                  {arrow === "up" ? "▲" : "▼"}
                </span>
              )}
              {isTeam ? (
                <div className="flex items-center -space-x-2 flex-shrink-0" data-testid={`contender-team-avatars-${r.team_id}`}>
                  {(r.members || []).map((mm, i) => (
                    <button key={mm.wrestler_id || i} type="button" data-testid={`contender-avatar-link-${mm.wrestler_id}`}
                            onClick={() => openProfile(mm.account_name)} title={mm.account_name ? `View ${mm.account_name}'s profile` : ""}
                            className="relative cursor-pointer transition-transform duration-150 hover:scale-110 hover:z-10" style={{ zIndex: (r.members || []).length - i }}>
                      <AvatarLayered showCrown
                        fgSrc={`${api.defaults.baseURL}/wrestlers/${mm.wrestler_id}/avatar?v=${new Date(mm.avatar_updated_at || 0).getTime()}`}
                        promo={mm.promotion}
                        className="w-7 h-7 rounded-md border-2"
                        style={{ borderColor: color, boxShadow: `0 0 4px ${color}66` }}
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <button type="button" data-testid={`contender-avatar-link-${r.wrestler_id}`}
                        onClick={() => openProfile(r.account_name)} title={r.account_name ? `View ${r.account_name}'s profile` : ""}
                        className="shrink-0 cursor-pointer transition-transform duration-150 hover:scale-110">
                  <AvatarLayered showCrown
                    fgSrc={`${api.defaults.baseURL}/wrestlers/${r.wrestler_id}/avatar?v=${new Date(r.avatar_updated_at || 0).getTime()}`}
                    promo={r.promotion}
                    className={`${first ? "w-11 h-11 border-2 border-[#FFD700]" : "w-8 h-8 border border-[#39194D]"} rounded-md`}
                  />
                </button>
              )}
              <div className="min-w-0 flex-1">
                <div className={`font-graffiti truncate leading-tight ${first ? "text-base text-[#FFD700]" : "text-sm"}`} style={first ? { textShadow: "0 0 8px #FFD70088" } : undefined}>{isTeam ? r.team_name : r.name}</div>
                <div className="text-[9px] font-mono text-[#A89BCC] truncate">
                  {isTeam
                    ? (r.members || []).map(mm => mm.wrestler_name).filter(Boolean).join(" & ")
                    : r.account_name}
                </div>
              </div>
              <div className={`font-num leading-none ${first ? "text-xl" : "text-base"}`} style={{ color: first ? "#FFD700" : color, textShadow: `0 0 6px ${first ? "#FFD700" : color}88` }}>{r.contender_points}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PvPPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [shows, setShows] = useState([]);
  const [champions, setChampions] = useState({});
  const [wrestlers, setWrestlers] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rosterOpen, setRosterOpen] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [myPartner, setMyPartner] = useState(null);
  const [nextTitleShotAt, setNextTitleShotAt] = useState(null);
  const [resultsCollapsed, setResultsCollapsed] = useState(false);
  const flashRef = useRef({});
  const prevRegRef = useRef({});
  const pendingRegRef = useRef(new Set());
  const [, setBriefTick] = useState(0);
  useEffect(() => {
    if (!user?.titleshot_cooldown_until) return;
    const id = setInterval(() => setBriefTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [user?.titleshot_cooldown_until]);
  const markRegistered = (showId, on) => setShows(prev => prev.map(x => x.show_id !== showId || !!x.is_registered === on ? x
    : { ...x, is_registered: on, filled: Math.max(0, (x.filled || 0) + (on ? 1 : -1)) }));
  const [newestResultId, setNewestResultId] = useState(null);
  const prevNewestRef = useRef(null);
  const hofHasPending = Object.values(user?.hof_level_up_pending || {}).some((v) => Number(v) > 0);
  const hofPendingTotal = Object.values(user?.hof_level_up_pending || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  const showSigRef = useRef(null);
  const auxBusyRef = useRef(false);
  const auxPendingRef = useRef(false);

  // Heavy data (roster, results, team, wallet): on load, on show transitions, after actions, and every 10s.
  const loadAux = async () => {
    if (auxBusyRef.current) { auxPendingRef.current = true; return; }
    auxBusyRef.current = true;
    try {
      const [w, c, tt] = await Promise.all([
        api.get("/wrestlers"),
        api.get("/pvp/completed"),
        api.get("/tag-teams/mine"),
      ]);
      // Sort roster: HW, MW, CW, TAG, WILDCARD — never by level
      const ORDER = { heavyweight: 0, middleweight: 1, cruiserweight: 2, tag_team: 3, tag_team_1: 3, wildcard: 4 };
      const sorted = [...w.data].sort((a, b) => (ORDER[a.class_id] ?? 99) - (ORDER[b.class_id] ?? 99));
      setWrestlers(sorted);
      setCompleted(c.data);
      // Highlight the newest result briefly when a new one lands at the top.
      const topId = c.data?.[0]?.show_id || null;
      if (topId && topId !== prevNewestRef.current) {
        prevNewestRef.current = topId;
        setNewestResultId(topId);
        setTimeout(() => setNewestResultId(cur => (cur === topId ? null : cur)), 1200);
      }
      setMyTeam(tt.data?.team || null);
      setMyPartner(tt.data?.partner || null);
      // Keep auth-user (titleshots_stored, currencies, streak) in sync so the cash-in button pops in instantly
      refresh();
      // Auto-select first eligible wrestler ONLY if we don't already have one selected
      setSelectedId(prev => {
        if (prev && sorted.some(x => x.wrestler_id === prev)) return prev;
        const first = sorted.find(x => x.class_id !== "tag_team" && x.class_id !== "tag_team_1");
        return first ? first.wrestler_id : prev;
      });
    } catch (e) { /* silent */ } finally {
      auxBusyRef.current = false;
      if (auxPendingRef.current) { auxPendingRef.current = false; loadAux(); }
    }
  };

  // Light 1.5s poll: shows + champions only.
  const loadShows = async () => {
    try {
      const ps = await api.get("/pvp/state");
      // Flash newly-available registers
      const newShows = ps.data.shows.map(x => pendingRegRef.current.has(x.show_id) && !x.is_registered
        ? { ...x, is_registered: true, filled: (x.filled || 0) + 1 } : x);
      newShows.forEach(s => {
        const prev = prevRegRef.current[s.show_type];
        if (prev && (prev.is_registered || prev.status !== "open") && !s.is_registered && s.status === "open") {
          flashRef.current[s.show_type] = Date.now();
        }
        prevRegRef.current[s.show_type] = s;
      });
      // Never let the shows list "disappear" during the tiny window between simulate+seed.
      // If the new fetch is missing types we saw before, keep those entries from the previous state.
      setShows(prev => {
        const byType = new Map(newShows.map(s => [s.show_type, s]));
        const KNOWN = ["proving_ground", "free_4_all", "tag_team_turmoil", "rumble_royale", "titleshot_bank", "promotion_wars"];
        KNOWN.forEach(t => {
          if (!byType.has(t)) {
            const carryover = prev.find(x => x.show_type === t);
            if (carryover) byType.set(t, carryover);
          }
        });
        return [...KNOWN.map(t => byType.get(t)).filter(Boolean), ...[...byType.values()].filter(x => !KNOWN.includes(x.show_type))];
      });
      setChampions(ps.data.champions || {});
      setNextTitleShotAt(ps.data.next_title_shot_at || null);
      // A show finished / new one opened → pull fresh results + roster stats right away.
      const sig = newShows.map(x => x.show_id).sort().join(",");
      if (showSigRef.current !== null && sig !== showSigRef.current) loadAux();
      showSigRef.current = sig;
    } catch (e) { /* silent */ }
  };

  const load = () => Promise.all([loadShows(), loadAux()]);

  useEffect(() => {
    load();
    const stopShows = visibleInterval(loadShows, 1500);
    const stopAux = visibleInterval(loadAux, 10000);
    return () => { stopShows(); stopAux(); };
    // eslint-disable-next-line
  }, []);

  const selected = useMemo(() => wrestlers.find(w => w.wrestler_id === selectedId), [wrestlers, selectedId]);

  const canRegisterShow = (show) => {
    if (!selected && show.show_type !== "tag_team_turmoil") return false;
    if (show.show_type === "tag_team_turmoil") return !!myTeam;
    if (show.show_type === "proving_ground" && selected && selected.level >= 100) return false;
    if (selected && (selected.class_id === "tag_team" || selected.class_id === "tag_team_1")) {
      return show.show_type === "tag_team_turmoil";
    }
    return true;
  };

  const register = async (show) => {
    try {
      let wid = selected?.wrestler_id;
      if (show.show_type === "tag_team_turmoil") {
        // find my tag team wrestler
        const tt = wrestlers.find(w => w.class_id === "tag_team" || w.class_id === "tag_team_1");
        if (!tt) return toast.error("You need a Tag Team wrestler");
        if (!myTeam) return toast.error("Form a Tag Team first");
        wid = tt.wrestler_id;
      }
      if (pendingRegRef.current.has(show.show_id)) return;
      pendingRegRef.current.add(show.show_id);
      markRegistered(show.show_id, true);
      try {
        await api.post(`/shows/${show.show_id}/register`, { wrestler_id: wid });
      } catch (e) {
        pendingRegRef.current.delete(show.show_id);
        markRegistered(show.show_id, false);
        throw e;
      }
      toast.success(`Registered for ${SHOW_META[show.show_type]?.label}`);
      await load();
      pendingRegRef.current.delete(show.show_id);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const registerPromotionWars = async () => {
    try {
      await api.post("/pvp/promotion-wars/register");
      toast.success("Your promotion is registered for Promotion Wars!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const useStoredTitleshot = async () => {
    // Prefer the currently selected wrestler; if a tag team is selected implicitly (via myTeam), cash in on our tag team wrestler
    let wid = selected?.wrestler_id;
    if (!wid && myTeam) {
      const tt = wrestlers.find(w => w.class_id === "tag_team" || w.class_id === "tag_team_1");
      wid = tt?.wrestler_id;
    }
    if (!wid) return toast.error("Select a wrestler first");
    try {
      const r = await api.post(`/pvp/cash-titleshot/${wid}`);
      const divLabel = ({ heavyweight: "Heavyweight", middleweight: "Middleweight", cruiserweight: "Cruiserweight", tag_team: "Tag Team" }[r.data?.division] || "");
      toast.success(`${divLabel} title shot incoming — watch chat!`);
      load();
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const briefcaseLeft = user?.titleshot_cooldown_until
    ? Math.max(0, Math.ceil((new Date(user.titleshot_cooldown_until).getTime() - Date.now()) / 1000)) : 0;

  const regularShows = shows.filter(s => SHOW_META[s.show_type]?.regular);
  const specialShows = shows.filter(s => !SHOW_META[s.show_type]?.regular);

  return (
    <>
    <div className="fixed inset-0 z-0 pointer-events-none bg-cover bg-center" data-testid="pvp-page-bg" aria-hidden="true"
         style={{ backgroundImage: "linear-gradient(180deg, rgba(10,5,26,0.55), rgba(10,5,26,0.8)), url('/backgroundgraphics/pvpshowspage_background.webp')" }} />
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true"><PageSparkles /><div className="wr-grain absolute inset-0" /></div>
    <div className="relative z-[1] max-w-[1600px] mx-auto px-3 py-3" data-testid="pvp-page">
      <div className="grid grid-cols-12 gap-3 lg:h-[calc(100dvh-118px)]">
        {/* LEFT — player + wrestlers */}
        <Panel className="col-span-12 lg:col-span-3 lg:min-h-0" innerClass="p-3 flex flex-col lg:min-h-0" colors={["#FF007F", "#9D00FF", "#5B3BFF"]}>
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-xl p-[3px] shrink-0" style={{ background: "linear-gradient(140deg, #FF007F, #9D00FF, #00F0FF)", boxShadow: "0 0 16px #FF007F55" }}>
              <AvatarLayered showCrown
                fgSrc={playerAvatar(user?.account_name, user?.player_avatar_updated_at)}
                promo={user?.promotion}
                className="w-[88px] h-[88px] rounded-[9px]"
                testId="pvp-player-avatar"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-graffiti text-2xl leading-none truncate" style={{ color: "#FF007F", textShadow: "0 0 12px #FF007F99, 2px 2px 0 #000" }}>{user?.account_name}</div>
                <button
                  data-testid="hof-page-btn"
                  onClick={() => user?.hof_unlocked ? nav("/hof") : null}
                  disabled={!user?.hof_unlocked}
                  title={user?.hof_unlocked ? (hofHasPending ? "Statue leveled up! Visit the Hall of Fame" : "Open Hall of Fame") : "Locked — defeat Bulk Hogan on training stage 50 to unlock"}
                  className={`relative w-8 h-8 rounded border-2 flex items-center justify-center transition-all hover:scale-110 disabled:cursor-not-allowed shrink-0 ${hofHasPending ? "hof-btn-pending" : ""}`}
                  style={{
                    borderColor: user?.hof_unlocked ? "#FFD700" : "#695D8E",
                    borderStyle: user?.hof_unlocked ? "solid" : "dashed",
                    background: user?.hof_unlocked ? "#0A051A" : "#1A0F2E",
                    boxShadow: user?.hof_unlocked ? (hofHasPending ? "0 0 14px #FFD700, 0 0 24px #FF007FAA" : "0 0 10px #FFD70088") : "none",
                    opacity: user?.hof_unlocked ? 1 : 0.55,
                  }}
                  aria-label={user?.hof_unlocked ? (hofHasPending ? "Hall of Fame — statue leveled up" : "Hall of Fame") : "Hall of Fame locked"}
                >
                  <img
                    src={`/hof/Statue%20${(user?.hof_selected_statue || "heavyweight").replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}.png`}
                    alt="HOF"
                    className="w-6 h-6 object-contain"
                    style={{ filter: user?.hof_unlocked ? "none" : "grayscale(1) brightness(0.5)" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  {!user?.hof_unlocked && (
                    <span
                      data-testid="hof-locked-overlay"
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{ background: "rgba(10,5,26,0.55)" }}
                    >
                      <Lock size={13} style={{ color: "#A89BCC" }} />
                    </span>
                  )}
                  {hofHasPending && (
                    <span
                      data-testid="hof-pending-badge"
                      className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-1 rounded-full font-heading text-[8px] flex items-center justify-center"
                      style={{
                        background: "#FF007F",
                        color: "#0A051A",
                        border: "1px solid #FFD700",
                        boxShadow: "0 0 8px #FF007F, 0 0 14px #FFD700AA",
                      }}
                    >
                      {hofPendingTotal}
                    </span>
                  )}
                </button>
              </div>
              <div className="mt-1.5 text-[10px] font-mono text-[#A89BCC] flex items-center gap-1">Streak <Flame size={12} className="text-[#FF9500]" style={{ filter: "drop-shadow(0 0 4px #FF4500)" }} /><span className="font-num text-sm text-[#FFE600]">{user?.win_streak || 0}</span></div>
              <div className="text-xs font-num flex items-center gap-2.5">
                <StatDelta value={user?.pvp_wins || 0} tone="up-green">
                  <StatIcon icon={Swords} color="#39FF14" value={user?.pvp_wins || 0} title="PvP Wins" big />
                </StatDelta>
                <StatDelta value={user?.pvp_losses || 0} tone="down-red">
                  <StatIcon icon={Skull} color="#FF007F" value={user?.pvp_losses || 0} title="PvP Losses" big />
                </StatDelta>
              </div>
            </div>
          </div>
          {(user?.titleshots_stored > 0) && (
            <button
              onClick={useStoredTitleshot}
              disabled={briefcaseLeft > 0}
              data-testid="use-stored-titleshot"
              className="w-full mb-3 rounded-md border-2 border-[#9D00FF] overflow-hidden enabled:hover:scale-[1.02] transition-transform flex flex-col disabled:cursor-not-allowed"
              style={{ boxShadow: briefcaseLeft > 0 ? "none" : "0 0 12px #9D00FF77" }}
              title={briefcaseLeft > 0 ? "Briefcase cooldown — next cash-in available when the timer runs out" : "Cash in a stored title shot with your selected wrestler"}
            >
              <div className="text-center py-1 bg-[#0A051A]">
                <div className="font-heading text-xs text-[#FFE600] uppercase tracking-widest" style={{ textShadow: "0 0 8px #FFE600" }}>CASH IN TITLESHOT × {user.titleshots_stored}</div>
                {briefcaseLeft > 0
                  ? <div data-testid="briefcase-cooldown" className="text-[9px] font-mono text-[#FF007F]">cooldown {Math.floor(briefcaseLeft / 60)}:{String(briefcaseLeft % 60).padStart(2, "0")}</div>
                  : <div className="text-[9px] font-mono text-[#00F0FF]">uses selected wrestler</div>}
              </div>
              <div className="w-full h-24 flex items-center justify-center p-2 bg-gradient-to-b from-[#1A0F2E] to-[#0A051A]" style={{ filter: briefcaseLeft > 0 ? "grayscale(0.8) brightness(0.6)" : "none" }}>
                <img
                  src="/pvp/briefcase.webp"
                  alt="Titleshot In The Bank"
                  className="w-full h-full object-contain"
                />
              </div>
            </button>
          )}
          <SectionHeading icon={Users} color="#00F0FF" small className="mb-2">My Roster</SectionHeading>
          <div className="flex flex-col gap-1.5 lg:flex-1 lg:min-h-0 lg:overflow-y-auto pr-1 pvp-scroll [&>*]:shrink-0">
            {wrestlers.length === 0 && (
              <div className="text-xs text-[#A89BCC] py-3 text-center">No wrestlers yet</div>
            )}
            {wrestlers.map(w => (
              <WrestlerSelectCard
                key={w.wrestler_id}
                w={w}
                active={selectedId === w.wrestler_id || (myTeam && (w.class_id === "tag_team" || w.class_id === "tag_team_1"))}
                team={(w.class_id === "tag_team" || w.class_id === "tag_team_1") ? myTeam : null}
                partner={(w.class_id === "tag_team" || w.class_id === "tag_team_1") ? myPartner : null}
                onSelect={(x) => setSelectedId(x.wrestler_id)}
                onOpenTagTeam={(tid) => nav(`/tag-team/${tid}`)}
              />
            ))}
          </div>
        </Panel>

        {/* CENTER — chat + shows */}
        <div className="col-span-12 lg:col-span-6 flex flex-col lg:min-h-0 lg:overflow-y-auto pvp-scroll">
          <ChampionsBar champions={champions} onOpenHolder={(c) => {
            if (c?.holder_team_id) nav(`/tag-team/${c.holder_team_id}`);
            else if (c?.holder_account_name) nav(`/profile/${encodeURIComponent(c.holder_account_name)}`);
          }} />
          <div className="mb-2">
            <ChatBox compact />
          </div>
          <SectionHeading icon={Swords} color="#00F0FF" small className="mb-1.5">Regular Shows</SectionHeading>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {regularShows.map(s => (
              <ShowCard
                key={s.show_id}
                show={s}
                canRegister={canRegisterShow(s)}
                onRegister={register}
                onOpenRoster={setRosterOpen}
                flash={flashRef.current[s.show_type] && (Date.now() - flashRef.current[s.show_type] < 900)}
              />
            ))}
          </div>
          <SectionHeading icon={Star} color="#FFE600" small className="mb-1.5">Special Events</SectionHeading>
          <div className="grid grid-cols-3 gap-2">
            {specialShows.map(s => s.show_type === "promotion_wars" ? (
              <PromotionWarsCard key={s.show_id} show={s} onRegister={registerPromotionWars} onOpenRoster={setRosterOpen} />
            ) : (
              <ShowCard
                key={s.show_id}
                show={s}
                canRegister={canRegisterShow(s)}
                onRegister={register}
                onOpenRoster={setRosterOpen}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — recent results */}
        <Panel className="col-span-12 lg:col-span-3 lg:min-h-0" innerClass="p-3 lg:overflow-y-auto pvp-scroll" colors={["#00F0FF", "#5B3BFF", "#9D00FF"]} delay={120}>
          <div className="flex items-center justify-between mb-2">
            <SectionHeading icon={Trophy} color="#FFE600" small className="mb-0">Recent Results</SectionHeading>
            <button
              onClick={() => setResultsCollapsed(v => !v)}
              className="p-1 rounded-md border border-[#FFE600]/40 text-[#FFE600] hover:bg-[#FFE600]/10 transition"
              title={resultsCollapsed ? "Expand" : "Collapse"}
              aria-label={resultsCollapsed ? "Expand results" : "Collapse results"}
              data-testid="results-collapse-toggle"
            >
              {resultsCollapsed ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
            </button>
          </div>
          <div
            className={`flex flex-col gap-1.5 ${resultsCollapsed ? "" : "max-h-[40vh] overflow-y-auto"} pr-1`}
            data-testid="recent-results"
          >
            {completed.length === 0 && (
              <div className="text-xs text-[#A89BCC] py-3 text-center">Nothing completed yet</div>
            )}
            {(resultsCollapsed ? completed.slice(0, 1) : completed).map((c) => {
              const meta = SHOW_META[c.show_type] || {};
              const isNewest = c.show_id === newestResultId;
              // Find "your" match for player highlight
              const myMatch = (c.results?.matches || []).find(m =>
                (m.a?.user_id === user?.user_id) || (m.b?.user_id === user?.user_id) ||
                (m.a?.members?.some(x => x.user_id === user?.user_id)) || (m.b?.members?.some(x => x.user_id === user?.user_id))
              );
              // Was I on side "a"?
              const iAmA = myMatch && ((myMatch.a?.user_id === user?.user_id) || myMatch.a?.members?.some(x => x.user_id === user?.user_id));
              const mine = myMatch ? (iAmA ? myMatch.a : myMatch.b) : null;
              const opp  = myMatch ? (iAmA ? myMatch.b : myMatch.a) : null;
              const myWin = myMatch && ((myMatch.winner_user_id && myMatch.winner_user_id === user?.user_id) || (myMatch.winner_team_id && myMatch.winner_team_id === (mine?.team_id)));
              const verb = myWin ? "beat" : "lost to";
              const tagWord = myMatch?.result_tag === "UPSET" ? "Upset Win" : myMatch?.result_tag === "DECISIVE" ? "Decisive Victory" : "Close Match";
              const nameMe = mine?.team_name || mine?.wrestler_name || "You";
              const nameOpp = opp?.team_name || opp?.wrestler_name || "opponent";
              return (
                <div
                  key={c.show_id}
                  className={`rounded-lg p-2 border-l-4 border transition-colors hover:bg-[#1C0D40] ${isNewest ? "result-pop" : ""}`}
                  style={{ borderColor: (meta.color || "#39194D") + "66", borderLeftColor: meta.color || "#39194D", background: "#0A051A" }}
                  data-testid={`result-${c.show_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-graffiti text-sm leading-tight truncate" style={{ color: meta.color, textShadow: `0 0 6px ${meta.color}66` }}>{c.name}</div>
                    <button
                      onClick={() => setRosterOpen({ show_id: c.show_id, name: c.name, slots: (c.results?.matches?.length || 0) * 2, matches: c.results?.matches || [], match_type: c.match_type, show_type: c.show_type, results: c.results })}
                      className="text-[9px] font-mono text-[#A89BCC] hover:neon-cyan"
                    >[view]</button>
                  </div>
                  {c.show_type === "promotion_wars" && c.results?.winner && (
                    <div className="text-[10px] font-mono mt-0.5 truncate" data-testid={`pw-result-${c.show_id}`}>
                      <span className="text-[#FFD700]">👑 {c.results.winner.name}</span>
                      <span className="text-[#A89BCC]"> won · {(c.results.participants || []).length} promotions</span>
                    </div>
                  )}
                  {myMatch && (
                   <div className="text-[10px] font-mono mt-0.5" data-testid={`my-result-${c.show_id}`}>
                    <span className={myWin ? "text-[#39FF14]" : "text-[#FF007F]"}>{nameMe}</span>
                    <span className="text-[#A89BCC]">{myWin ? " defeated " : " lost to "}</span>
                    <span className="text-[#00F0FF]">{nameOpp}</span>
                    <span className="text-[#A89BCC]"> · {tagWord}</span>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
          <ContendersBlock selected={selected} myTeam={myTeam} nextTitleShotAt={nextTitleShotAt} />
        </Panel>
      </div>

      <RosterModal show={rosterOpen} onClose={() => setRosterOpen(null)} />

      <style>{`
        @keyframes hof-btn-pending-pulse {
          0%, 100% {
            box-shadow: 0 0 10px #FFD700, 0 0 20px #FF007F99;
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 20px #FFD700, 0 0 36px #FF007F, 0 0 48px #FF007F77;
            transform: scale(1.08);
          }
        }
        .hof-btn-pending {
          animation: hof-btn-pending-pulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
    </>
  );
}
